import React, { useMemo, useState } from 'react';
import {
  useEmailNotificationRoleAccess,
  useNovuWorkflowOptions,
  useReplaceEmailNotificationRoleAccess,
} from '@/api/EmailNotifications';
import { useUserTypes } from '@/api/Users';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '@/api/queryKeys';
import { EmailNotificationRoleAccess } from '@/types';
import { App, Button, Space, Tag, Tooltip } from 'antd';
import {
  CustomColumns,
  CustomTable,
  faEdit,
  faUserCheck,
  Icon,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { EmailNotificationAccessPopup } from './EmailNotificationAccessPopup';

type GroupRow = {
  key: string;
  userType: string;
  userRole: string;
  workflows: EmailNotificationRoleAccess[];
};

export const EmailNotificationAccessDataGrid: React.FC = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();

  const { data: rows, isFetching } = useEmailNotificationRoleAccess();
  const { data: workflows } = useNovuWorkflowOptions();
  const { data: userTypes } = useUserTypes();
  const { mutateAsync: replaceMutate } =
    useReplaceEmailNotificationRoleAccess();

  const [editing, setEditing] = useState<{
    userType?: string;
    userRole?: string;
  }>();

  const grouped = useMemo(() => {
    const map = new Map<string, EmailNotificationRoleAccess[]>();
    (rows || []).forEach((r) => {
      const key = `${r.userType}|${r.userRole}`;
      const list = map.get(key) || [];
      list.push(r);
      map.set(key, list);
    });
    return Array.from(map.entries()).map(([key, list]) => ({
      key,
      userType: list[0].userType,
      userRole: list[0].userRole,
      workflows: list,
    })) as GroupRow[];
  }, [rows]);

  const columns: CustomColumns<GroupRow> = [
    {
      title: t('userType'),
      dataIndex: 'userType',
    },
    {
      title: t('role'),
      dataIndex: 'userRole',
    },
    {
      title: t('emailNotificationTypes'),
      dataIndex: 'workflows',
      render: (_, record) => (
        <Space wrap>
          {record.workflows.map((o) => (
            <Tag key={o.novuWorkflow} className="py-1 m-0">
              {o.description || o.novuWorkflow}
              {o.allowUserModify ? (
                <Tooltip title={t('allowUserToModify')}>
                  <Icon
                    icon={faUserCheck}
                    className="text-success ms-1"
                    size="xs"
                  />
                </Tooltip>
              ) : null}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: t('actions'),
      dataIndex: 'key',
      render: (_, record) => (
        <Button
          type="link"
          icon={<Icon icon={faEdit} />}
          onClick={() =>
            setEditing({
              userType: record.userType,
              userRole: record.userRole,
            })
          }
        />
      ),
    },
  ];

  const handleSubmit = async (payload: {
    userType: string;
    userRole: string;
    items: { novuWorkflow: string; allowUserModify: boolean }[];
  }) => {
    const result = await replaceMutate({
      userType: payload.userType,
      userRole: payload.userRole,
      items: payload.items as never,
    });
    if (!result.success) {
      return result.message || 'Failed to save';
    }
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.emailNotificationRoleAccess],
    });
    message.success(t('save'));
    return undefined;
  };

  return (
    <>
      <CustomTable
        rowKey="key"
        loading={isFetching}
        columns={columns}
        dataSource={grouped}
        onAdd={() => setEditing({})}
      />
      {editing ? (
        <EmailNotificationAccessPopup
          data={rows}
          userTypeRoles={userTypes}
          workflows={workflows}
          editUserType={editing.userType}
          editUserRole={editing.userRole}
          onSubmit={handleSubmit}
          onCancel={() => setEditing(undefined)}
        />
      ) : null}
    </>
  );
};
